import React, { useCallback, useMemo, useState } from "react";
import { useSnackbarController } from "@rebasepro/app";
import { Button as RebaseButton, cls, iconSize } from "@rebasepro/ui";
import { ChevronLeft as RebaseChevronLeftIcon } from "lucide-react";

const Button = RebaseButton as React.ComponentType<any>;
const ChevronLeftIcon = RebaseChevronLeftIcon as React.ComponentType<any>;

/**
 * The chrome every workspace view shares: the two-pane frame, what it does
 * when two panes no longer fit, and the announcements a screen reader needs
 * but a sighted user never sees.
 *
 * Candidatos, Prospectos, Correo and Ofertas encontradas were four copies of
 * the same `w-96 shrink-0` list beside a `flex-1` detail. Four copies meant the
 * same defects four times over, so the frame lives here once and the views
 * spell their intent — "this is the list", "the user opened a row" — rather
 * than restating the layout.
 */

/**
 * The sheet, which is what this is: the content area the scaffold hands us.
 *
 * It used to be `bg-white dark:bg-surface-900`, chosen back when a surface was
 * a light/dark pair of numbered greys and the only recessed option,
 * `surface-50`, put `--color-text-secondary` at 4.41:1 — under AA for every
 * count, timestamp and email address these four views write with
 * `color="secondary"`. Both halves of that reasoning have since expired: the
 * kit names surfaces by ROLE now, and `surface-900` is the value of the FRAME,
 * so the workspace was painting the app's darkest grey *inside* the sheet and
 * every card sat in a hole. The light sheet is #f9f9fa and the secondary text
 * token moved to #737373, which measures 4.54:1 there.
 *
 * Cards inside are one step lifted from this, which is where the structure now
 * comes from; the rail keeps its border because a rail is an edge, not a card.
 */
export const workspaceRootCls = "flex h-full w-full overflow-hidden bg-surface-sheet";

/**
 * The list rail's scrolling body.
 *
 * `pt-1` is load-bearing, and it is the whole reason this is shared rather than
 * spelled four times. `overflow-y-auto` computes `overflow-x` to `auto` as
 * well, so the box clips on all four sides, and the selected card's mark is
 * `ring-1` — a box-shadow drawn one pixel OUTSIDE the border box. With no top
 * padding the first card's border box started exactly on the clip edge, so
 * selecting the top row of any of these queues drew three sides of a rectangle.
 * Four pixels is the same gap `space-y-1` puts between the cards, so the rail
 * reads as it did; `px-2` already gave the ring its room on the other two
 * sides.
 */
export const workspaceListCls = "flex-1 overflow-y-auto px-2 pt-1 pb-4 space-y-1";

/**
 * A master-detail pair that can only show one pane at a time.
 *
 * The breakpoint is `lg` (1024px) rather than a phone width because that is
 * where THIS layout breaks: below it the 384px rail leaves the detail under
 * 640px, narrower than the `max-w-3xl` the detail's own content asks for.
 *
 * `detailOpen` is deliberately NOT "something is selected". All four views
 * auto-select the first visible row so the detail pane is never empty on a
 * desktop, and reusing that signal here would drop a phone user straight into
 * an arbitrary record with the list already gone. This tracks the one thing
 * that actually means "show me the detail": the user opened a row.
 */
export function useMasterDetail() {
    const [detailOpen, setDetailOpen] = useState(false);
    const openDetail = useCallback(() => setDetailOpen(true), []);
    const closeDetail = useCallback(() => setDetailOpen(false), []);

    // `max-lg:hidden` rather than `hidden lg:flex`: the override then exists
    // ONLY below the breakpoint, and each pane keeps whatever `display` it
    // already had at `lg` and up. Spelling the shown state as a display
    // utility instead would put two `display` classes on the same element,
    // and which of them wins is decided by their order in Tailwind's
    // stylesheet — not by their order in the class attribute — so the pane
    // that needs `flex` would silently get `block` on one of these views.
    return {
        detailOpen,
        openDetail,
        closeDetail,
        /** The list rail: full width, and stood aside once the detail is open. */
        masterCls: cls(
            "w-full lg:w-96 shrink-0 border-r flex flex-col h-full",
            detailOpen && "max-lg:hidden"
        ),
        /** The detail pane: the rail's mirror, so exactly one of the two shows. */
        detailCls: cls(
            "flex-1 overflow-y-auto",
            !detailOpen && "max-lg:hidden"
        )
    };
}

/**
 * The view's name, for screen readers only.
 *
 * The collection toolbar above these views draws the visible title, so drawing
 * a second one is the duplication `TalentsQueue` already refuses in its own
 * comment. What the toolbar does not draw is a heading that can be navigated
 * to: without this, each workspace opened as a flat run of `h6`s under no
 * heading at all.
 */
export function WorkspaceTitle({ children }: { children: React.ReactNode }) {
    return <h1 className="sr-only">{children}</h1>;
}

/**
 * The way back to the list, for the widths where the list is not on screen.
 */
export function BackToList({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <div className={cls(
            "lg:hidden sticky top-0 z-10 p-2 border-b",
            "bg-surface-sheet border-hairline"
        )}>
            <Button size="small" variant="text" onClick={onClick}>
                <ChevronLeftIcon size={iconSize.smallest}/> {label}
            </Button>
        </div>
    );
}

/**
 * The snackbar, plus the live region that makes it audible.
 *
 * Every outcome in these views — saved, sent, imported, failed — is reported
 * only by the snackbar, and the snackbar carries no live region of its own, so
 * a screen reader announced none of them. Wrapping `open` rather than editing
 * thirty-odd call sites means an outcome added later is announced without
 * anyone having to remember to.
 */
export function useAnnouncedSnackbar() {
    const snackbar = useSnackbarController() as any;
    const [announcement, setAnnouncement] = useState("");

    const open = useCallback((props: { type?: string; message: string }) => {
        // Cleared first, then set on the next frame: two identical outcomes in
        // a row are two events, and a live region that never changes value
        // announces the second one as nothing at all.
        setAnnouncement("");
        requestAnimationFrame(() => setAnnouncement(props.message));
        snackbar.open(props);
    }, [snackbar]);

    const wrapped = useMemo(() => ({ ...snackbar, open }), [snackbar, open]);

    const liveRegion = (
        <div role="status" aria-live="polite" className="sr-only">{announcement}</div>
    );

    return { snackbar: wrapped, liveRegion };
}
